import type {
  DashboardBulkTradeRun,
  DashboardOptimizer,
  DashboardPortfolioReport,
} from '../types';
import { buildPortfolioDashboardData } from './actionQueue';

const TERMINAL_OPTIMIZER_STATES = new Set(['COMPLETED', 'CANCELED', 'FAILED']);
const TERMINAL_BULK_RUN_STATES = new Set([
  'COMPLETED',
  'COMPLETED_PENDING_PTFS',
  'CANCELED',
]);

export type DashboardOperationsStats = {
  activeOptimizers: DashboardOptimizer[];
  activeBulkRuns: DashboardBulkTradeRun[];
  totalActive: number;
};

export type DashboardTradeStats = {
  initiated: number;
  ready: number;
  requested: number;
  total: number;
};

export type DashboardStatsBundle = {
  portfolioStats: ReturnType<typeof buildPortfolioDashboardData>['stats'];
  actionQueue: ReturnType<typeof buildPortfolioDashboardData>['actionQueue'];
  operationsStats: DashboardOperationsStats;
  tradeStats: DashboardTradeStats;
  reports: DashboardPortfolioReport[];
  optimizers: DashboardOptimizer[];
  bulkRuns: DashboardBulkTradeRun[];
};

type DashboardStatsQueryData = {
  portfolioReports?: { portfolioReports?: DashboardPortfolioReport[] };
  activeOptimizers?: { portfolioOptimizers?: DashboardOptimizer[] };
  activeBulkRuns?: { bulkTradeRuns?: DashboardBulkTradeRun[] };
  initiatedTrades?: { totalCount?: number };
  readyTrades?: { totalCount?: number };
  requestedTrades?: { totalCount?: number };
};

/** Shared derivations from `FETCH_DASHBOARD_STATS` for v1 + v2 Today's Dashboard tabs. */
export function deriveDashboardStats(
  statsData: DashboardStatsQueryData | undefined | null,
): DashboardStatsBundle {
  const reports = statsData?.portfolioReports?.portfolioReports || [];
  const optimizers = statsData?.activeOptimizers?.portfolioOptimizers || [];
  const bulkRuns = statsData?.activeBulkRuns?.bulkTradeRuns || [];
  const { stats: portfolioStats, actionQueue } =
    buildPortfolioDashboardData(reports);

  const activeOptimizers = optimizers.filter(
    (o) => !TERMINAL_OPTIMIZER_STATES.has(o.state),
  );
  const activeBulkRuns = bulkRuns.filter(
    (r) => !TERMINAL_BULK_RUN_STATES.has(r.state),
  );

  const initiated = statsData?.initiatedTrades?.totalCount || 0;
  const ready = statsData?.readyTrades?.totalCount || 0;
  const requested = statsData?.requestedTrades?.totalCount || 0;

  return {
    portfolioStats,
    actionQueue,
    operationsStats: {
      activeOptimizers,
      activeBulkRuns,
      totalActive: activeOptimizers.length + activeBulkRuns.length,
    },
    tradeStats: {
      initiated,
      ready,
      requested,
      total: initiated + ready + requested,
    },
    reports,
    optimizers,
    bulkRuns,
  };
}
